import {
  arrayToEntityDict,
  courseOrganizationId,
  type Assignment,
  type Course,
  type CourseOrganization,
  type DeletableEntity,
  type Entity,
  type EntityDict,
  type LinkedModuleBuilds,
  type LinkedPlayerBuilds,
  type UserAssignmentStatus,
  type UserCoursePerformance
} from '@webapp2/shared/model'
import { postGetBody } from '@webapp2/shared/services/api'
import { capitalize, toRaw } from 'vue'
import {
  state,
  type ModuleBuildRegistrationState,
  type MultiplayerSession,
  type PlayerTechnique,
  type Prebrief,
  type State
} from '../state'
import { cache } from './cache'
import { ensureLinkedModuleBuilds } from './linked-module-builds-service'
import { ensureMultiplayerSessionDependencies } from './multiplayer-session-service'

let initialUpdateResolve: () => void
const etags: { [id: string]: string | undefined } = {}

export const initialUpdate = new Promise<void>(resolve => (initialUpdateResolve = resolve))

export async function setStateFromCache() {
  const [
    organizationId,
    courses,
    courseOrganizations,
    multiplayerSessions,
    playerTechniques,
    prebriefs,
    registeredBuilds,
    userCoursePerformances,
    linkedModuleBuilds,
    linkedPlayerBuilds,
    assignments,
    userAssignmentStatuses
  ] = await Promise.all([
    cache.get<string>('currentOrganizationId'),
    cache.getByType<Course>('course'),
    cache.getByType<CourseOrganization>('courseOrganization'),
    cache.getByType<MultiplayerSession>('multiplayerSession'),
    cache.getByType<PlayerTechnique>('playerTechnique'),
    cache.getByType<Prebrief>('prebrief'),
    cache.getByType<ModuleBuildRegistrationState>('RegisteredState'),
    cache.getByType<UserCoursePerformance>('UserCoursePerformance'),
    cache.getByType<LinkedModuleBuilds>('LinkedModuleBuilds'),
    cache.getByType<LinkedPlayerBuilds>('LinkedPlayerBuilds'),
    cache.getByType<Assignment>('assignment'),
    cache.getByType<UserAssignmentStatus>('userAssignmentStatus')
  ])

  state.organizationId = organizationId

  state.courses = courses
  state.courseOrganizations = courseOrganizations
  state.multiplayerSessions = multiplayerSessions
  state.playerTechniques = playerTechniques
  state.prebriefs = prebriefs
  state.userCoursePerformances = userCoursePerformances
  state.registeredStates = registeredBuilds
  state.linkedModuleBuilds = linkedModuleBuilds
  state.linkedPlayerBuilds = linkedPlayerBuilds
  state.assignments = assignments
  state.userAssignmentStatuses = userAssignmentStatuses

  await loadCourseDependencies(Object.values(courses))
}

async function loadCourseDependencies(courses: Course[]) {
  await ensureLinkedModuleBuilds(courses.map(x => x.linkedModuleBuildsId))
}

export function updateEntities(loadAll = false) {
  return updateByTypes(Object.keys(typeToStateKey) as EntityType[], loadAll)
}

function getByTypes(types: string[], loadAll: boolean) {
  return postGetBody<DocumentsByOrganizationResponse<EntityDict<Entity>>>(
    `organization/${state.organizationId}/documents`,
    types.map(x => {
      return { type: capitalize(x), etag: loadAll ? undefined : etags[x] }
    })
  )
}

export async function updateByTypes(types: EntityType[], loadAll = false): Promise<void> {
  const response = await getByTypes(types, loadAll)
  if (!response) {
    console.log(`Failed to update via API using cache for type: ${types.join(', ')}`)
    return
  }

  if (response.course?.documents) {
    await loadCourseDependencies(Object.values(response.course.documents as EntityDict<Course>))
  }

  const merges = types.map(type => mergeDocuments(type, response[type], loadAll))
  const unreleasedCourseIds = types.includes('courseOrganization') ? removeUnreleasedCourses() : []
  const etagsByType = Object.fromEntries(
    types.flatMap(type => {
      const etag = response[type]?.etag
      return etag ? [[type, etag]] : []
    })
  )
  Object.assign(etags, etagsByType)
  await cache.setItems(merges.flatMap(x => x.cacheItems))
  await cache.deleteItems([...merges.flatMap(x => x.cacheDeleteIds), ...unreleasedCourseIds])

  await ensureMultiplayerSessionDependencies()
  initialUpdateResolve()
}

function mergeDocuments<K extends EntityType>(type: K, documentSet: DocumentSet<Entity> | undefined, loadAll: boolean) {
  const stateKey = typeToStateKey[type]
  const docs: EntityDict<DeletableEntity> = documentSet?.documents ?? {}
  if (loadAll) {
    state[stateKey] = docs as State[typeof stateKey]
    void cache.deleteByType(type)
    return { cacheItems: Object.values(toRaw(state[stateKey])) as { id: string }[], cacheDeleteIds: [] as string[] }
  }
  const deletedIds = Object.values(docs)
    .filter(x => x.deleted)
    .map(x => x.id)
  const merged: EntityDict<Entity> = { ...state[stateKey], ...docs }
  deletedIds.forEach(id => delete merged[id])
  state[stateKey] = merged as State[typeof stateKey]
  return { cacheItems: Object.values(docs).filter(x => !x.deleted), cacheDeleteIds: deletedIds }
}

function removeUnreleasedCourses(): string[] {
  const unreleasedCourseIds = Object.keys(state.courses).filter(
    x => !(courseOrganizationId(x, state.organizationId!) in state.courseOrganizations)
  )
  unreleasedCourseIds.forEach(x => delete state.courses[x])
  return unreleasedCourseIds
}

export async function ensureById<T extends Entity>(type: string, store: EntityDict<T>, ids: string[]): Promise<T[]> {
  const idsToLoad = [...new Set(ids)].filter(x => !(x in store))
  if (idsToLoad.length === 0) {
    return []
  }
  const response = await postGetBody<T[]>('documents-by-id', { type, ids: idsToLoad })
  if (!response) {
    return []
  }
  Object.assign(store, arrayToEntityDict(response))
  return response
}

export interface DocumentSet<T extends Entity> {
  etag?: string
  documents: EntityDict<T>
  isAll: boolean
}

export type DocumentsByOrganizationResponse<T extends Record<string, Entity>> = {
  [K in keyof T]?: DocumentSet<T[K]>
}

export type EntityType = keyof typeof typeToStateKey

const typeToStateKey = {
  prebrief: 'prebriefs',
  course: 'courses',
  courseOrganization: 'courseOrganizations',
  multiplayerSession: 'multiplayerSessions',
  userCoursePerformance: 'userCoursePerformances',
  playerTechnique: 'playerTechniques',
  assignment: 'assignments',
  userAssignmentStatus: 'userAssignmentStatuses'
} as const

export const releaseDerivedTypes: EntityType[] = ['course', 'prebrief', 'playerTechnique', 'userCoursePerformance']
